import { useQuery } from "@tanstack/react-query";

import { AZURE_MJ } from "@/lib/axios";

const PLANT_ITEMS_KEYS = {
  getAllInternalPlantItems: "GET_ALL_INTERNAL_PLANT_ITEMS",
  getAllInternalPlantHireLines: "GET_ALL_INTERNAL_PLANT_HIRE_LINES",
  getAllInternalPlantHireLinesForProject:
    "GET_ALL_INTERNAL_PLANT_HIRE_LINES_FOR_PROJECT",
  getAllExternalPlantHireLines: "GET_ALL_EXTERNAL_PLANT_HIRE_LINES",
  getAllExternalPlantHireLinesForProject:
    "GET_ALL_EXTERNAL_PLANT_HIRE_LINES_FOR_PROJECT"
};

export interface IInternalPlantItem {
  "@odata.etag": string;

  no: string;
  description: string;
  description2: string;
  searchDescription: string;

  unitOfMeasure: string;
  internalPlantCategory: string;
  internalPlantCategoryDescription: string;
  internalPlantPostingGroup: string;
  genProdPostingGroup: string;
  vatProdPostingGroup: string;

  lastDateModified: string;
  status: string;

  datePurchased: string | null;

  totalPlantQuantity: number;
  qtyAvailableToHire: number;
  qtyOnHire: number;

  directUnitCost: number;
  indirectUnitCost: number;
  unitCost: number;

  rechargeDamaged: boolean;
  rechargeLost: boolean;
  rechargeUnitCostDamaged: number;
  rechargeUnitCostLost: number;

  comment: boolean;
  blocked: boolean;
  linkedConsumables: boolean;
  ignorePlantHolidayDates: boolean;

  fixedAssetNo: string;
  globalDimension1Code: string;
  costCode: string;
  noSeries: string;
  location: string;

  registrationNumber: string;
  chassisNumber: string;
  engineNumber: string;

  motExpiryDate: string | null;
  roadTaxExpiryDate: string | null;

  vinNumber: string;
  radioCode: string;
  colour: string;
  paintCode: string;
  make: string;
  model: string;
  serialNumber: string;

  serviceHistoryLineNo: number;
  serviceItemGroupEQ2: string;

  lastKnownJobNo: string;
  lastKnownJobSiteAddress: string;

  image: string;
  picture: string;
}

export interface IInternalPlantHireLine {
  "@odata.etag": string;

  documentType: string;
  documentNo: string;
  lineNo: number;

  requestedByUser: string;

  projectNo: string;
  projectName: string;
  projectTaskNo: string;
  projectTaskDescription: string;
  regionCode: string;

  type: string;
  no: string;

  description: string;
  description2: string;
  workType: string;

  unitCost: number;

  onHireDate: string;
  offHireDate: string;
  expectedOffHireDate: string;
  lastCostDate: string;
  recordInsertionTime: string;

  hireQuantity: number;
  unitOfMeasureCode: string;
  currentOnHireQuantity: number;

  hirePeriod: number;
  initialHirePeriod: number;

  transferToJobNo: string;
  transferToJobTaskNo: string;
  transferOnHireDate: string | null;

  quantityToTransfer: number;

  partialOffHireDate: string | null;
  partialOffHireQuantity: number;

  offHiredTransfersToDate: number;

  offHireReference: string;

  quantityDamaged: number;
  quantityLost: number;
  quantityLostToDate: number;

  shortcutDimension1Code: string;
  costCode: string;
  yourReference: string;

  dimensionSetID: number;

  onHold: boolean;
  completed: boolean;
  userAccessEQ2: boolean;
  worksheetErrorExists: boolean;

  status: string;
  locationCode: string;

  linkedToLineNo: number;

  siteRequisitionDocumentNo: string;
  siteRequisitionLineNo: number;
  siteReqLineCreatedBy: string;
}

export interface IExternalPlantHireLine {
  "@odata.etag": string;

  documentType: string;
  documentNo: string;
  lineNo: number;

  no: string;
  description: string;

  projectName: string;
  onHoldEQ2: boolean;
  status: string;

  onHireDateEQ2: string | null;
  expectedOffHireDateEQ2: string;
  offHireDateEQ2: string | null;

  hireQuantityEQ2: number;
  unitOfMeasure: string;

  unitCost: number;
  hirePeriod: number;

  lineAmount: number;

  costCode: string;
  projectNo: string;
  projectTaskNo: string;
  projectTaskDescription: string;

  buyFromVendorNo: string;
  buyFromVendorName: string;
}

export const useGetAllInternalPlantItems = () => {
  return useQuery({
    queryKey: [PLANT_ITEMS_KEYS.getAllInternalPlantItems],
    queryFn: async (): Promise<{
      "@odata.context": string;
      value: IInternalPlantItem[];
    }> => {
      const { data } = await AZURE_MJ.get(
        `/bc/internalPlantItems?filter=status eq 'Available' and qtyAvailableToHire gt 0`
      );
      return data;
    }
  });
};

export const useGetAllInternalPlantHireLines = () => {
  return useQuery({
    queryKey: [PLANT_ITEMS_KEYS.getAllInternalPlantHireLines],
    queryFn: async (): Promise<{
      "@odata.context": string;
      value: IInternalPlantHireLine[];
    }> => {
      const { data } = await AZURE_MJ.get("/bc/internalPlantHireLines");
      return data;
    }
  });
};

export const useGetAllInternalPlantHireLinesForProject = (project?: string) => {
  return useQuery({
    queryKey: [
      PLANT_ITEMS_KEYS.getAllInternalPlantHireLinesForProject,
      project
    ],
    queryFn: async (): Promise<{
      "@odata.context": string;
      value: IInternalPlantHireLine[];
    }> => {
      const { data } = await AZURE_MJ.get(
        `/bc/internalPlantHireLines?projectNumber=${project}`
      );
      return data;
    },
    enabled: !!project
  });
};

export const useGetAllExternalPlantHireLines = () => {
  return useQuery({
    queryKey: [PLANT_ITEMS_KEYS.getAllExternalPlantHireLines],
    queryFn: async (): Promise<{
      "@odata.context": string;
      value: IExternalPlantHireLine[];
    }> => {
      const { data } = await AZURE_MJ.get("/bc/externalPlantHireLines");
      return data;
    }
  });
};

export const useGetAllExternalPlantHireLinesForProject = (project?: string) => {
  return useQuery({
    queryKey: [
      PLANT_ITEMS_KEYS.getAllExternalPlantHireLinesForProject,
      project
    ],
    queryFn: async (): Promise<{
      "@odata.context": string;
      value: IExternalPlantHireLine[];
    }> => {
      const { data } = await AZURE_MJ.get(
        `/bc/externalPlantHireLines?projectNumber=${project}`
      );
      return data;
    },
    enabled: !!project
  });
};
